import { type PointerEvent, useState } from "react";
import { StatusLegend } from "./Charts";
import { clock } from "./format";
import { usePref } from "./hooks";
import { Icon } from "./icons";
import { detailHref } from "./links";
import {
  formatUptime,
  type Overview as OverviewModel,
  type OverviewRange,
  STATUS_LABEL,
  type ViewModel,
} from "./model";

const RANGE_LABEL: Record<OverviewRange, string> = { "24h": "24 h", "30d": "30 days" };
const SLOT_WORD: Record<OverviewRange, string> = { "24h": "hours", "30d": "days" };

const day = (t: number) =>
  new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short" });

function slotLabel(overview: OverviewModel, i: number): string {
  const start = overview.slotStarts[i] ?? 0;
  const end = start + overview.slotMs;
  return overview.range === "24h"
    ? `${clock(start)}–${clock(end)}`
    : `${day(start)} ${clock(start)} – ${day(end)} ${clock(end)}`;
}

/** Axis labels: every 6 hours, or every 7 days ending today. */
function axis(overview: OverviewModel): { i: number; label: string }[] {
  const n = overview.slotStarts.length;
  if (overview.range === "24h") {
    return overview.slotStarts.flatMap((t, i) =>
      new Date(t).getHours() % 6 === 0 ? [{ i, label: `${clock(t)}` }] : [],
    );
  }
  return [n - 1, n - 8, n - 15, n - 22, n - 29]
    .filter((i) => i >= 0)
    .map((i) => ({
      i,
      label: i === n - 1 ? "today" : day((overview.slotStarts[i] ?? 0) + overview.slotMs),
    }));
}

/**
 * Every site's status over time in one grid: rows are sites, cells are hours (24 h) or days
 * (30 d) colored by the worst result in them. The quickest answer to "was anything down, and when?".
 */
export function Overview({ vm }: { vm: ViewModel }) {
  const [range, setRange] = usePref<OverviewRange>("overviewRange", "24h", ["24h", "30d"]);
  const [visible, setVisible] = usePref("overview", "shown", ["shown", "hidden"] as const);
  const [hover, setHover] = useState<{ row: number; slot: number; x: number; y: number } | null>(
    null,
  );
  if (!vm.overview) return null;
  const overview = vm.overview[range];
  const n = overview.slotStarts.length;
  const many = SLOT_WORD[range];

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const cell = (event.target as HTMLElement).closest<HTMLElement>("[data-slot]");
    if (!cell) return setHover(null);
    const box = event.currentTarget.getBoundingClientRect();
    setHover({
      row: Number(cell.dataset.row),
      slot: Number(cell.dataset.slot),
      x: event.clientX - box.left,
      y: cell.getBoundingClientRect().top - box.top,
    });
  };

  const hovered = hover ? overview.rows[hover.row] : undefined;
  const hoveredSlot = hover && hovered ? hovered.slots[hover.slot] : undefined;
  const failed = hoveredSlot ? hoveredSlot.counts.down + hoveredSlot.counts.degraded : 0;
  const total = hoveredSlot
    ? hoveredSlot.counts.up + hoveredSlot.counts.degraded + hoveredSlot.counts.down
    : 0;

  return (
    <section className="overview panel" aria-labelledby="overview-title">
      <div className="overview-head">
        <h2 id="overview-title" className="eyebrow">
          Overview
        </h2>
        {visible === "shown" && (
          <>
            <fieldset className="segmented small">
              <legend className="sr-only">Overview range</legend>
              {(["24h", "30d"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  aria-pressed={range === r}
                  onClick={() => setRange(r)}
                >
                  {RANGE_LABEL[r]}
                </button>
              ))}
            </fieldset>
            <StatusLegend />
          </>
        )}
        <button
          type="button"
          className="linkbtn overview-toggle"
          aria-expanded={visible === "shown"}
          onClick={() => setVisible(visible === "shown" ? "hidden" : "shown")}
        >
          {visible === "shown" ? "Hide" : "Show"}
        </button>
      </div>

      {visible === "shown" && (
        <div
          className="overview-grid"
          onPointerMove={onPointerMove}
          onPointerLeave={() => setHover(null)}
        >
          <ul>
            {overview.rows.map((row, r) => (
              <li
                key={row.name}
                className="overview-row"
                data-status={row.status}
                aria-label={`${row.name}: ${formatUptime(row.uptime)} available over the last ${RANGE_LABEL[range]}; failures in ${row.failingSlots} of ${n} ${many}.`}
              >
                <a className="overview-name" href={detailHref(row.name)}>
                  <Icon name={row.status} label={STATUS_LABEL[row.status]} />
                  <span className="ellipsis">{row.name}</span>
                </a>
                <div
                  className="overview-cells"
                  aria-hidden="true"
                  style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}
                >
                  {row.slots.map((slot, i) => (
                    <span
                      // biome-ignore lint/suspicious/noArrayIndexKey: slots are positional
                      key={i}
                      className="overview-cell"
                      data-status={slot.status}
                      data-row={r}
                      data-slot={i}
                      data-active={hover?.row === r && hover.slot === i}
                    />
                  ))}
                </div>
                <span className="overview-uptime num">{formatUptime(row.uptime)}</span>
              </li>
            ))}
          </ul>
          <div className="overview-axis" aria-hidden="true">
            <span />
            <div className="overview-ticks" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
              {axis(overview).map((tick) => (
                <span key={tick.i} style={{ gridColumn: `${tick.i + 1}` }}>
                  {tick.label}
                </span>
              ))}
            </div>
            <span />
          </div>
          {hover && hovered && hoveredSlot && (
            <div
              className="tooltip overview-tooltip"
              role="status"
              style={{ left: hover.x, top: hover.y }}
            >
              <p className="tooltip-value">{hovered.name}</p>
              <p
                className="tooltip-status"
                data-status={hoveredSlot.status === "none" ? "unknown" : hoveredSlot.status}
              >
                <Icon name={hoveredSlot.status === "none" ? "unknown" : hoveredSlot.status} />
                {hoveredSlot.status === "none" ? "No data" : STATUS_LABEL[hoveredSlot.status]}
                {failed > 0 && (
                  <span className="muted num">
                    {" "}
                    · {failed} of {total} results
                  </span>
                )}
              </p>
              <p className="muted num">{slotLabel(overview, hover.slot)}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
